import type { ActionDetail } from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { getAction, stopAction } from '../api/actions.js';
import { isApiError } from '../api/client.js';
import { useCurrentUser } from '../auth/session-context.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { PageHeader } from '../components/PageHeader.js';
import { ResultCounts } from '../components/ResultCounts.js';
import {
  describeAction,
  describeTarget,
  isOpen,
  reasonOf,
  STATUS_LABEL,
  STATUS_TONE,
} from '../lib/actions.js';
import { effectiveTimezone, formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { Loading } from '../ui/Skeleton.js';

/**
 * One action and what each server did with it (knowledge doc section 12, step 5:
 * "React shows per-server results").
 *
 * While any server has not answered, the page asks again every few seconds, so a
 * ban reads as done without anyone reloading; it stops asking once every command
 * is final. **Stop** is section 12's "can be stopped midway": it cancels the
 * commands no agent has collected, and says plainly that one already collected
 * may still run.
 */

/** A long-polling agent collects a command within a second or two of it existing. */
const REFRESH_MS = 3_000;

const anyOpen = (action: ActionDetail | undefined): boolean =>
  action?.results.some((result) => isOpen(result.status)) ?? false;

export function ActionDetailPage(): ReactNode {
  const { id = '' } = useParams();
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  const canAct = session.user.role !== 'viewer';
  const [confirming, setConfirming] = useState(false);
  const queryClient = useQueryClient();

  const action = useQuery({
    queryKey: ['action', id],
    queryFn: () => getAction(id),
    refetchInterval: (query) => (anyOpen(query.state.data) ? REFRESH_MS : false),
  });

  const stop = useMutation({
    mutationFn: () => stopAction(id),
    onSuccess: () => {
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: ['action', id] });
      void queryClient.invalidateQueries({ queryKey: ['actions'] });
    },
  });

  if (action.isPending) {
    return (
      <div className="page">
        <Loading />
      </div>
    );
  }

  if (action.isError) {
    return (
      <div className="page">
        <PageHeader title="Action" />
        <Alert tone="danger" title="Could not load that action">
          {isApiError(action.error) && action.error.status === 404
            ? 'There is no action with that id.'
            : 'Try again in a moment.'}{' '}
          <Link to="/actions">Back to actions</Link>.
        </Alert>
      </div>
    );
  }

  const detail = action.data;
  const waiting = detail.results.filter((result) => result.status === 'pending').length;
  const reason = reasonOf(detail);

  return (
    <div className="page">
      <PageHeader
        title={describeAction(detail)}
        description={`On ${describeTarget(detail.target)}`}
        actions={
          canAct &&
          detail.stoppedAt === null &&
          waiting > 0 && (
            <Button
              variant="danger"
              onClick={() => {
                setConfirming(true);
              }}
            >
              Stop
            </Button>
          )
        }
      />

      <Card title="Asked for">
        <dl className="details">
          <dt>By</dt>
          <dd>
            {detail.requestedByEmail ?? <span className="muted">{detail.source}</span>}
            {detail.source === 'policy' && (
              <span className="cell-sub">
                Automatic: this task was switched on to apply itself, by this administrator.
              </span>
            )}
          </dd>
          <dt>When</dt>
          <dd>
            {formatInstant(detail.createdAt, { timezone })}
            <span className="cell-sub">{formatRelative(detail.createdAt)}</span>
          </dd>
          {reason !== undefined && (
            <>
              <dt>Reason</dt>
              <dd>{reason}</dd>
            </>
          )}
          <dt>Servers had until</dt>
          <dd>
            {formatInstant(detail.expiresAt, { timezone })}
            <span className="cell-sub">
              A server that has not collected it by then never runs it.
            </span>
          </dd>
          {detail.stoppedAt !== null && (
            <>
              <dt>Stopped</dt>
              <dd>{formatInstant(detail.stoppedAt, { timezone })}</dd>
            </>
          )}
        </dl>
      </Card>

      {detail.warnings.length > 0 && (
        <Alert tone="warning" title="Worth knowing">
          <ul>
            {detail.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Card
        title="Per server"
        description={
          anyOpen(detail)
            ? 'Updating by itself while servers are still working on it.'
            : 'Every server has answered, or its time ran out.'
        }
        aside={<ResultCounts counts={detail.counts} />}
      >
        <div className="table-wrap">
          <table className="table">
            <caption className="visually-hidden">What each server did</caption>
            <thead>
              <tr>
                <th scope="col">Server</th>
                <th scope="col">Result</th>
                <th scope="col">What it said</th>
                <th scope="col">Finished</th>
              </tr>
            </thead>
            <tbody>
              {detail.results.map((result) => (
                <tr key={result.commandId}>
                  <td>
                    <Link to={`/servers/${encodeURIComponent(result.serverId)}`}>
                      {result.name}
                    </Link>
                    {result.serverStatus !== 'active' && (
                      <span className="cell-sub">{result.serverStatus}</span>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${STATUS_TONE[result.status]}`}>
                      {STATUS_LABEL[result.status]}
                    </span>
                  </td>
                  <td className="text-sm">
                    {result.detail ?? <span className="muted">nothing</span>}
                  </td>
                  <td className="text-sm">
                    {result.completedAt === null ? (
                      <span className="muted">—</span>
                    ) : (
                      <span title={formatInstant(result.completedAt, { timezone })}>
                        {formatRelative(result.completedAt)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {confirming && (
        <ConfirmDialog
          title="Stop this action?"
          description={`${String(waiting)} server${waiting === 1 ? ' has' : 's have'} not collected it yet and will not run it. A server that already has it may still finish.`}
          confirmLabel="Stop"
          danger
          busy={stop.isPending}
          error={
            stop.isError
              ? stop.error instanceof Error
                ? stop.error.message
                : 'Could not stop it.'
              : null
          }
          onConfirm={() => {
            stop.mutate();
          }}
          onCancel={() => {
            setConfirming(false);
            stop.reset();
          }}
        />
      )}
    </div>
  );
}
